// A card opened large: body, tags, where it appears, the place it's tied to (or, for a place, the cards
// tied to it), reactions, comments and delete.
import { useState, type FormEvent } from 'react';
import type * as Y from 'yjs';
import { boardsMap, cardsMap, cleanTags, parseCard, reactionsByEmoji } from '../../shared/doc.js';
import { cardLabel, cardsAtPlace, listPlaces } from '../../shared/map.js';
import { addComment, cardBoards, deleteCard, deleteComment, toggleReaction, updateCard } from '../../shared/ops.js';
import type { ProjectConn } from '../doc/useProject.js';
import { useYVersion } from '../doc/useY.js';
import { href } from '../router.js';
import { CardBody } from './CardBody.js';
import { embedUrl } from './embed.js';

const EMOJI = ['👍', '❤️', '🔥', '🤔', '✅'];
const when = (iso: string) => new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

export function CardPanel({ conn, projectId, cardId, readOnly = false, onOpenCard, onClose }: {
  conn: ProjectConn;
  projectId: string;
  cardId: string;
  readOnly?: boolean;
  /** Opens another card in this panel (a place's linked cards). */
  onOpenCard?: (cardId: string) => void;
  onClose: () => void;
}) {
  const cards = cardsMap(conn.doc);
  useYVersion(cards);
  useYVersion(conn.doc.getMap('meta'));
  const cardY = cards.get(cardId) as Y.Map<any> | undefined;
  const [editing, setEditing] = useState(false);
  const [comment, setComment] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (!cardY) {
    const tomb = conn.doc.getMap('meta').get(`deleted:${cardId}`) as { by: string } | undefined;
    return (
      <aside className="card-panel" role="dialog" aria-label="Card">
        <p role="alert">This card was deleted{tomb ? ` by ${tomb.by}` : ''}.</p>
        <button onClick={onClose}>Close</button>
      </aside>
    );
  }
  const card = parseCard(cardY.toJSON());
  const boards = cardBoards(conn.doc, cardId).map((id) => boardsMap(conn.doc).get(id)?.get('title') as string);
  const embed = card?.kind === 'link' ? embedUrl(card.url) : null;
  const places = listPlaces(conn.doc);
  const linkedPlace = card?.placeCardId ? places.find((p) => p.id === card.placeCardId) : undefined;
  const linked = card?.kind === 'place' ? cardsAtPlace(conn.doc, cardId) : [];
  const send = (e: FormEvent) => {
    e.preventDefault();
    addComment(conn.doc, conn.me, cardId, comment);
    setComment('');
  };

  return (
    <aside className="card-panel" role="dialog" aria-label="Card">
      <header>
        <strong>{card?.kind ?? 'Card'}</strong>
        <button className="link" onClick={onClose} aria-label="Close card">×</button>
      </header>
      <div className="panel-body" onDoubleClick={() => !readOnly && setEditing(true)}>
        <CardBody conn={conn} projectId={projectId} cardId={cardId} editing={editing} onDoneEditing={() => setEditing(false)} onClose={onClose} />
        {!readOnly && !editing && card && card.kind !== 'image' && card.kind !== 'link' && <button className="link small" onClick={() => setEditing(true)}>Edit</button>}
        {embed && <iframe className="embed" src={embed} title="Video" allow="fullscreen; picture-in-picture" sandbox="allow-scripts allow-same-origin allow-presentation" />}
      </div>
      {card && (
        <>
          <p className="muted">
            Added by {card.createdBy.kind === 'agent' ? '🤖 ' : ''}
            {card.createdBy.name} · {when(card.createdAt)}
            {card.updatedAt !== card.createdAt && ` · edited by ${card.updatedBy.name}`}
          </p>
          {boards.length > 0 && <p className="muted">On: {boards.join(', ')}</p>}
          {card.kind === 'place' ? (
            <section className="linked" aria-label="Linked cards">
              <h3>Linked cards ({linked.length})</h3>
              {linked.length ? (
                <ul>
                  {linked.map((id) => {
                    const c = parseCard(cards.get(id)?.toJSON());
                    const label = c ? cardLabel(c) : 'Card';
                    return (
                      <li key={id}>
                        {onOpenCard ? <button className="link" onClick={() => onOpenCard(id)}>{label}</button> : label} <small className="muted">{c?.kind}</small>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="muted small">Tie a card to this place from that card's own panel.</p>
              )}
            </section>
          ) : readOnly ? (
            linkedPlace && (
              <p className="muted">
                Place: <a href={href.place(projectId, linkedPlace.id)} onClick={(e) => { e.stopPropagation(); onClose(); }}>{linkedPlace.name || 'Untitled place'}</a>
              </p>
            )
          ) : (
            <>
              {linkedPlace && (
                <p className="muted">
                  <a href={href.place(projectId, linkedPlace.id)} onClick={(e) => { e.stopPropagation(); onClose(); }}>📍 {linkedPlace.name || 'Untitled place'}</a>
                </p>
              )}
              <label className="place-link">
                Place
                <select aria-label="Place" value={card.placeCardId ?? ''} onChange={(e) => updateCard(conn.doc, conn.me, cardId, { placeCardId: e.target.value || null })}>
                  <option value="">None</option>
                  {card.placeCardId && !linkedPlace && <option value={card.placeCardId}>(a deleted place)</option>}
                  {places.map((p) => (
                    <option key={p.id} value={p.id}>{p.name || 'Untitled place'}</option>
                  ))}
                </select>
              </label>
            </>
          )}
          {!readOnly && (
            <label className="tags">
              Tags
              <input
                defaultValue={card.tags.join(', ')}
                placeholder="village, ferry"
                onBlur={(e) => updateCard(conn.doc, conn.me, cardId, { tags: cleanTags(e.target.value.split(',')) })}
              />
            </label>
          )}
          <div className="reactions" role="group" aria-label="Reactions">
            {EMOJI.map((emoji) => {
              const who = reactionsByEmoji(card.reactions).find((r) => r.emoji === emoji)?.userIds ?? [];
              return (
                <button key={emoji} aria-pressed={who.includes(conn.me.userId)} onClick={() => toggleReaction(conn.doc, conn.me, cardId, emoji)}>
                  {emoji} {who.length || ''}
                </button>
              );
            })}
          </div>
          <section className="comments" aria-label="Comments">
            {card.comments.map((c) => (
              <article key={c.id}>
                <strong>{c.author.kind === 'agent' ? '🤖 ' : ''}{c.author.name}</strong> <small>{when(c.at)}</small>
                <p>{c.text}</p>
                {c.author.userId === conn.me.userId && c.author.kind === 'human' && (
                  <button className="link small" onClick={() => deleteComment(conn.doc, cardId, c.id)}>Delete</button>
                )}
              </article>
            ))}
            <form onSubmit={send}>
              <textarea aria-label="Write a comment" placeholder="Comment…" value={comment} onChange={(e) => setComment(e.target.value)} rows={2} />
              <button type="submit" disabled={!comment.trim()}>Comment</button>
            </form>
          </section>
          {!readOnly &&
            (confirmDelete ? (
              <div className="danger-row">
                <span>Delete this card from every board?</span>
                <button className="danger" onClick={() => { deleteCard(conn.doc, conn.me, cardId); onClose(); }}>Delete card</button>
                <button className="link" onClick={() => setConfirmDelete(false)}>Keep</button>
              </div>
            ) : (
              <button className="link danger" onClick={() => setConfirmDelete(true)}>Delete card…</button>
            ))}
        </>
      )}
    </aside>
  );
}
